import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, ChevronDown, ArrowRight, Sparkles } from 'lucide-react';

import { useLanguage } from '../context/LanguageContext';
/**
 * QuickRegionMenu — premium "Outside UAE" dropdown.
 * ─────────────────────────────────────────────────────────────
 * Compact 3-column grid with glassmorphism backdrop, real flag
 * SVGs (flagcdn 1×/2× srcSet) and elegant 14px country names.
 *
 * onPickCountry(country | null) — null means "country not listed"
 */
const REGIONS = [
  {
    key: 'gcc', titleAr: 'الخليج', titleEn: 'GCC',
    countries: [
      { code: 'sa', ar: 'السعودية',  en: 'Saudi Arabia' },
      { code: 'qa', ar: 'قطر',        en: 'Qatar'        },
      { code: 'kw', ar: 'الكويت',     en: 'Kuwait'       },
      { code: 'bh', ar: 'البحرين',   en: 'Bahrain'      },
      { code: 'om', ar: 'عُمان',       en: 'Oman'         },
    ],
  },
  {
    key: 'arab', titleAr: 'العالم العربي', titleEn: 'Arab World',
    countries: [
      { code: 'eg', ar: 'مصر',     en: 'Egypt'    },
      { code: 'jo', ar: 'الأردن',  en: 'Jordan'   },
      { code: 'lb', ar: 'لبنان',   en: 'Lebanon'  },
      { code: 'ma', ar: 'المغرب',  en: 'Morocco'  },
      { code: 'tn', ar: 'تونس',    en: 'Tunisia'  },
    ],
  },
  {
    key: 'world', titleAr: 'العالم', titleEn: 'International',
    countries: [
      { code: 'us', ar: 'أمريكا',     en: 'United States'  },
      { code: 'gb', ar: 'بريطانيا',   en: 'United Kingdom' },
      { code: 'fr', ar: 'فرنسا',     en: 'France'         },
      { code: 'de', ar: 'ألمانيا',  en: 'Germany'        },
      { code: 'ca', ar: 'كندا',      en: 'Canada'         },
    ],
  },
];

// High-quality flag image from flagcdn (CDN-cached, w40 = 40px wide)
const Flag = ({ code, alt }) => (
  <img
    src={`https://flagcdn.com/w40/${code}.png`}
    srcSet={`https://flagcdn.com/w40/${code}.png 1x, https://flagcdn.com/w80/${code}.png 2x`}
    width="22"
    height="16"
    alt={alt}
    loading="lazy"
    className="h-[16px] w-[22px] rounded-[2px] object-cover ring-1 ring-white/15 shrink-0 shadow-sm"
  />
);

const QuickRegionMenu = ({ language = 'ar', onPickCountry }) => {
  const { localize } = useLanguage();
  const isAr = language === 'ar';
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const t = (ar, en) => localize({ ar, en });

  const handlePick = (country) => {
    setOpen(false);
    onPickCountry?.(country);
  };

  return (
    <div className="relative" ref={ref} dir={isAr ? 'rtl' : 'ltr'} style={{ fontFamily: isAr ? "'Tajawal', system-ui" : "'Inter', system-ui" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="hidden md:inline-flex items-center gap-1.5 h-7 px-3 rounded-full hover:bg-white/[0.06] text-slate-200 hover:text-white text-[11px] font-semibold transition-all"
        aria-expanded={open}
        aria-haspopup="menu"
        data-testid="outside-uae-btn"
      >
        <span className="relative inline-flex items-center justify-center">
          <span
            className="absolute inset-0 rounded-full bg-indigo-400/40 blur-md motion-reduce:hidden"
            aria-hidden
            style={{ animation: 'lwt-globe-pulse 2.6s ease-in-out infinite' }}
          />
          <Globe className="relative w-4 h-4 text-indigo-200" strokeWidth={2.25} />
        </span>
        {t('خارج الإمارات؟', 'Outside UAE?')}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <style>{`
        @keyframes lwt-globe-pulse {
          0%, 100% { opacity: 0.45; transform: scale(0.95); }
          50%      { opacity: 0.85; transform: scale(1.15); }
        }
      `}</style>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            // Compact (≈40% smaller than the old 680px), glassmorphism,
            // anchored under the trigger; cap to viewport with a safe margin.
            className={`absolute z-50 mt-2 ${isAr ? 'right-0' : 'left-0'}
              w-[420px] max-w-[calc(100vw-24px)]
              rounded-2xl overflow-hidden
              bg-[rgba(13,17,23,0.78)] backdrop-blur-2xl
              ring-1 ring-white/10 border border-white/5
              shadow-[0_20px_60px_rgba(0,0,0,0.55)]`}
            role="menu"
            data-testid="outside-uae-menu"
          >
            {/* Header — slim & informative */}
            <div className="px-4 py-2.5 border-b border-white/5 bg-gradient-to-r from-indigo-950/40 via-slate-900/30 to-slate-900/30">
              <p className="text-[9px] font-bold tracking-[0.22em] uppercase text-indigo-300/80">
                {t('قائمة الانتظار العالمية', 'Global Waitlist')}
              </p>
              <p className="text-[12px] text-white/85 mt-0.5 leading-tight">
                {t('نخدم الإمارات حالياً — اختر دولتك', 'Currently serving UAE — pick your country')}
              </p>
            </div>

            {/* 3-column grid — tighter spacing */}
            <div className="grid grid-cols-3 divide-x divide-white/5 rtl:divide-x-reverse">
              {REGIONS.map((region) => (
                <div key={region.key} className="px-2.5 py-2.5">
                  <p className="text-[9px] font-bold tracking-[0.18em] uppercase text-slate-400 mb-1.5 px-1">
                    {localize({ ar: region.titleAr, en: region.titleEn })}
                  </p>
                  <div className="space-y-0.5">
                    {region.countries.map((c) => (
                      <button
                        key={c.code}
                        type="button"
                        onClick={() => handlePick({ ...c, code: c.code.toUpperCase() })}
                        className={`group w-full flex items-center gap-2 px-1.5 py-1 rounded-md hover:bg-white/[0.07] active:bg-white/[0.12] transition-colors text-${isAr ? 'right' : 'left'}`}
                        data-testid={`outside-uae-country-${c.code}`}
                      >
                        {/* Flag → leading edge: right in RTL, left in LTR (auto via flex+dir) */}
                        <Flag code={c.code} alt={localize({ ar: c.ar, en: c.en })} />
                        <span
                          className="flex-1 text-white font-medium truncate leading-tight"
                          style={{ fontSize: '14px' }}
                        >
                          {localize({ ar: c.ar, en: c.en })}
                        </span>
                        <ArrowRight
                          className={`w-3 h-3 text-white/25 group-hover:text-indigo-300 ${isAr ? 'rotate-180' : ''} transition-colors shrink-0`}
                          aria-hidden
                        />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Slim footer CTA */}
            <button
              type="button"
              onClick={() => handlePick(null)}
              className="w-full flex items-center justify-center gap-1.5 px-4 py-2 bg-gradient-to-r from-indigo-600/90 to-violet-600/90 hover:from-indigo-500 hover:to-violet-500 text-white text-[12px] font-semibold transition-all border-t border-white/10"
              data-testid="outside-uae-other-btn"
            >
              <Sparkles className="w-3 h-3" />
              {t('بلدي غير موجود — انضم', 'My country isn\'t listed — join anyway')}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QuickRegionMenu;
